/**
 * Onboarding (B-38, D-90) — the questions asked once, after the code is verified and
 * before the app is usable.
 *
 * One question per screen. The reason is the same one that put the tab bar at the bottom
 * (D-82): this is a phone held in one hand, and a single question with its answers as
 * large targets is the only form factor that does not become a scroll. It also means the
 * Continue button is always about the thing directly above it.
 *
 * What the questions ARE lives in `packages/core/src/onboarding.ts`, not here — the
 * order, the options, what counts as an answer, and what gets written. This file owns
 * how a question looks and nothing else, so the bot can one day ask the same things
 * without a second list to keep in sync (D-09).
 *
 * Nothing is written until the last Continue. A half-filled profile is a worse thing to
 * own than a flow that has to be started again, and `onboardedAt` landing in the same
 * write as the answers is what makes the gate honest.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AccountRole, FitnessGoal, OnboardingAnswers, OnboardingStep } from "@altrtrack/core";
import {
  ACCOUNT_ROLES,
  BODY_TYPES,
  FITNESS_GOALS,
  MAX_AGE,
  MIN_AGE,
  SEXES,
  SKIPPABLE_STEPS,
  isStepAnswered,
  stepsFor,
} from "@altrtrack/core";
import { completeOnboarding } from "../write.js";
import { checkNumber, type Bounds } from "../fields.js";
import { Choices, HealthQuestion } from "../components/Questions.js";
import { GymPicker } from "../components/GymPicker.js";

const AGE: Bounds = { min: MIN_AGE, max: MAX_AGE, integer: true, unit: "age" };
const WEIGHT: Bounds = { min: 20, max: 300, unit: "bodyweight" };

/** The heading and the line under it, per question. */
const ASKS: Record<OnboardingStep, { title: string; sub: string }> = {
  role: { title: "What brings you here?", sub: "So the app knows whose log this is." },
  sex: { title: "Your sex", sub: "Used for how your training is read back to you." },
  age: { title: "How old are you?", sub: "Stored as a birth year, so it stays right." },
  bodyweight: {
    title: "What do you weigh?",
    sub: "Every bodyweight exercise is scored against this. You can change it any time on Profile.",
  },
  bodyType: {
    title: "Which is closest to your build?",
    sub: "Helps shape a training plan later. You can skip this.",
  },
  goals: { title: "What are you training for?", sub: "Pick as many as apply. You can skip this." },
  health: {
    title: "Any injuries or past surgeries?",
    sub: "Only used to keep exercises that could hurt you out of a plan. You can skip this.",
  },
  name: { title: "What's your name?", sub: "How you'll be addressed." },
  gym: { title: "Which gym?", sub: "Find it by PIN code, or add it if it isn't listed." },
  gymLink: { title: "Google Maps link", sub: "Optional — paste it if you have it handy." },
};

export function OnboardingPage({ uid }: { uid: string }) {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<OnboardingAnswers>({});
  const [at, setAt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /*
   * Re-derived from the role on every render rather than held in state, so going back and
   * changing the role genuinely changes what is asked next — the case a stored list would
   * quietly get wrong. `at` is clamped for the same reason: an owner four questions deep
   * who returns and picks Trainee must not land past the end of a shorter list.
   */
  const steps: readonly OnboardingStep[] = answers.role
    ? ["role", ...stepsFor(answers.role)]
    : ["role"];
  const index = Math.min(at, steps.length - 1);
  const step = steps[index]!;
  const last = index === steps.length - 1 && answers.role !== undefined;
  const answered = isStepAnswered(step, answers);

  /*
   * Skipping clears the step's answer rather than leaving a half-made one behind: a
   * build tapped and then skipped past was not given. The last step finishes with the
   * cleared answers passed in directly, because the state update has not landed yet.
   */
  const skip = () => {
    const key = step === "bodyType" ? "bodyType" : step === "goals" ? "goals" : "health";
    const next = { ...answers };
    delete next[key];
    setAnswers(next);
    void forward(next);
  };

  const set = (patch: Partial<OnboardingAnswers>) => {
    setAnswers((a) => ({ ...a, ...patch }));
    setFailure(null);
  };

  /*
   * Clearing a numeric answer DELETES the key rather than setting it to `undefined`. With
   * `exactOptionalPropertyTypes` the two are different things, and only one of them is
   * what "this question has no answer" means — backspacing an age to empty has to make
   * `isStepAnswered` false and the Continue button go dead, which a present-but-undefined
   * key would also do, but which Firestore would later reject if it ever reached a write.
   */
  /*
   * Toggling a goal reads the CURRENT goals inside the updater, not from the render that
   * drew the button. Two goals tapped inside one render — which is what two quick taps
   * are — both computed their new list from the same stale array, so the second tap
   * silently discarded the first. Found by tapping two of them on the screen; no test
   * would have, because a test that clicks twice awaits a render in between.
   */
  const toggleGoal = (goal: FitnessGoal) => {
    setAnswers((a) => {
      const chosen = a.goals ?? [];
      return {
        ...a,
        goals: chosen.includes(goal) ? chosen.filter((g) => g !== goal) : [...chosen, goal],
      };
    });
    setFailure(null);
  };

  const setNumber = (key: "age" | "bodyweightKg", value: number | null) => {
    setAnswers((a) => {
      const next = { ...a };
      if (value === null) delete next[key];
      else next[key] = value;
      return next;
    });
    setFailure(null);
  };

  const forward = async (submitted: OnboardingAnswers = answers) => {
    if (saving) return;
    if (!last) {
      setAt(index + 1);
      return;
    }
    setSaving(true);
    const result = await completeOnboarding(uid, submitted, new Date());
    setSaving(false);
    if (result.ok) {
      // The profile subscription will drop the gate on its own; this only makes sure the
      // screen behind it is Today rather than whatever route the sign-in landed on.
      navigate("/", { replace: true });
    } else {
      setFailure(result.reason === "failed" ? result.detail : "That didn't save.");
    }
  };

  return (
    <main className="onboard">
      {/*
        The role question cannot say how many steps follow, because the role is what
        decides — and "Step 1 of …" reads as a bug rather than as an unknown. It says the
        step it is on until there is a total to give.
      */}
      <p className="label onboard-step">
        Step {index + 1}
        {answers.role ? ` of ${steps.length}` : ""}
      </p>

      <h1 className="title">{ASKS[step].title}</h1>
      <p className="sub onboard-sub">{ASKS[step].sub}</p>

      <div className="onboard-body" key={step}>
        {step === "role" && (
          <Choices
            options={ACCOUNT_ROLES}
            selected={answers.role}
            /*
             * Choosing a role advances on its own. It is the one question whose answer is
             * a single tap with no second thought, and making it the only question that
             * needs two taps to leave would be the odd one out.
             */
            onPick={(role: AccountRole) => {
              set({ role });
              setAt(1);
            }}
          />
        )}

        {step === "sex" && (
          <Choices options={SEXES} selected={answers.sex} onPick={(sex) => set({ sex })} />
        )}

        {step === "age" && (
          <Answer
            bounds={AGE}
            value={answers.age}
            suffix="years"
            inputMode="numeric"
            onChange={(age) => setNumber("age", age)}
          />
        )}

        {step === "bodyweight" && (
          <Answer
            bounds={WEIGHT}
            value={answers.bodyweightKg}
            suffix="kg"
            inputMode="decimal"
            onChange={(kg) => setNumber("bodyweightKg", kg)}
          />
        )}

        {step === "bodyType" && (
          <Choices options={BODY_TYPES} selected={answers.bodyType} onPick={(bodyType) => set({ bodyType })} />
        )}

        {step === "health" && (
          <HealthQuestion
            value={answers.health}
            onChange={(update) => {
              setAnswers((a) => {
                const health = update(a.health);
                const next = { ...a };
                if (health === undefined) delete next.health;
                else next.health = health;
                return next;
              });
              setFailure(null);
            }}
          />
        )}

        {step === "goals" && (
          <Choices
            options={FITNESS_GOALS}
            selected={answers.goals ?? []}
            onPick={toggleGoal}
          />
        )}

        {step === "name" && (
          <Text
            value={answers.name ?? ""}
            label="Your name"
            placeholder="Name"
            autoComplete="name"
            onChange={(name) => set({ name })}
          />
        )}

        {step === "gym" && (
          <GymPicker
            uid={uid}
            onPick={(gym) => set({ gym })}
            initialPin={answers.gym?.pinCode ?? ""}
            selected={answers.gym}
          />
        )}

        {step === "gymLink" && (
          <Text
            value={answers.gymMapsUrl ?? ""}
            label="Google Maps link"
            placeholder="https://maps.app.goo.gl/…"
            inputMode="url"
            autoComplete="url"
            onChange={(gymMapsUrl) => set({ gymMapsUrl })}
          />
        )}
      </div>

      {failure && (
        <div className="notice bad">
          <b>That didn't save.</b>
          <p className="sub">{failure}</p>
        </div>
      )}

      <div className="onboard-foot">
        {index > 0 && (
          <button className="btn" type="button" onClick={() => setAt(index - 1)} disabled={saving}>
            Back
          </button>
        )}
        {/*
          The role question has no Continue — picking an answer there IS continuing. Every
          other question keeps one, so a typed answer has somewhere to be confirmed.
        */}
        {SKIPPABLE_STEPS.has(step) && (
          <button className="btn" type="button" onClick={skip} disabled={saving}>
            Skip for now
          </button>
        )}
        {step !== "role" && (
          <button className="btn btn-primary" type="button" onClick={() => void forward()} disabled={!answered || saving}>
            {saving ? "Saving…" : last ? "Finish" : "Continue"}
          </button>
        )}
      </div>
    </main>
  );
}

/**
 * A number that may be unanswered.
 *
 * `NumberField` is not reused here because it is built around a value that always exists
 * and commits every valid keystroke — right for editing a set, wrong for a question that
 * starts with no answer at all. What it is actually worth sharing is the parsing, and
 * that is `checkNumber` (D-65), which this calls: an empty field stays empty rather than
 * becoming 0, a half-typed number is not an answer, and nothing is prefilled, because a
 * prefilled age is a plausible lie that a single tap would store.
 */
function Answer({
  bounds,
  value,
  suffix,
  inputMode,
  onChange,
}: {
  bounds: Bounds;
  value: number | undefined;
  suffix: string;
  inputMode: "numeric" | "decimal";
  onChange: (value: number | null) => void;
}) {
  const [draft, setDraft] = useState(value === undefined ? "" : String(value));
  const check = checkNumber(draft, bounds);
  // Nothing typed yet is not a mistake, so it is not reported as one.
  const problem = draft.trim() === "" ? null : check.message;

  return (
    <div className="answer">
      <div className="answer-line">
        <input
          className="input answer-input"
          value={draft}
          inputMode={inputMode}
          autoFocus
          aria-label={bounds.unit}
          aria-invalid={problem !== null}
          onChange={(e) => {
            setDraft(e.target.value);
            const next = checkNumber(e.target.value, bounds);
            onChange(next.problem === null ? next.value : null);
          }}
        />
        <span className="answer-suffix">{suffix}</span>
      </div>
      {problem && <p className="sub field-error">{problem}</p>}
    </div>
  );
}

function Text({
  value,
  label,
  placeholder,
  autoComplete,
  inputMode,
  onChange,
  problem = null,
}: {
  value: string;
  label: string;
  placeholder: string;
  autoComplete: string;
  inputMode?: "numeric" | "url";
  onChange: (value: string) => void;
  problem?: string | null;
}) {
  return (
    <div className="answer">
      <input
        className="input answer-input wide"
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoFocus
        aria-label={label}
        aria-invalid={problem !== null}
        {...(inputMode ? { inputMode } : {})}
        onChange={(e) => onChange(e.target.value)}
      />
      {problem && <p className="sub field-error">{problem}</p>}
    </div>
  );
}
